import { DEFAULT_ANIMATE_DURATION } from '@apeiron/temp'
import {
  Box,
  ButtonBase,
  ButtonBaseProps,
  styled,
  Typography
} from '@mui/material'
import LongButtonSVG from '@src/components/share/icon/LongButtonSVG'
import ShortButtonSVG from '@src/components/share/icon/ShortButtonSVG'
import { ButtonSVGType, OperateButton } from '@src/constants/variant'
import { FC } from 'react'

const StyledBasicButton = styled(ButtonBase)<ApeironOperateButtonProps>`
  height: 70px;
  width: 170px;
  transition: filter ${DEFAULT_ANIMATE_DURATION}ms;
  :hover {
    filter: brightness(1.5);
  }
  :active {
    filter: brightness(1.2);
  }
  :disabled {
    filter: grayscale(100%) brightness(0.45) contrast(0.83);
  }
`

const Label = styled(Typography)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: var(--font-ARW);
  font-size: 22px;
  text-shadow: 0px 2px 1px rgba(0, 0, 0, 0.4);
  white-space: nowrap;
`

const Button = styled(ButtonBase)`
  position: relative;
  padding: 0;
  border-radius: 0;
  background-color: transparent;
  color: transparent;
  overflow: hidden;

  & path#background {
    transition: fill ${DEFAULT_ANIMATE_DURATION}ms ease;
    fill: #74726c;
  }

  &:hover path#background {
    fill: #9a978d;
  }

  &:disabled #background {
    fill: #333536;
  }

  & .spade {
    transition: fill 0.5s ease;
    fill: #414141;
  }

  &:hover .spade {
    fill: #414141;
  }

  :disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .blue {
    transition: filter 0.5s ease;
  }

  :hover .blue {
    filter: brightness(1.5);
  }
  :active .blue {
    filter: brightness(1.2);
  }
`

const ApeironText = styled(Typography)`
  font-family: var(--font-Apeiron);
  color: #838383;
  font-size: 20px;
`

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const SvgComponent: FC<SvgComponentProps> = props => {
  const { variant, disabled } = props

  switch (variant) {
    case OperateButton.Short: {
      return <ShortButtonSVG />
    }

    case OperateButton.BlueShort: {
      if (disabled) {
        return <ShortButtonSVG />
      }

      return <ShortButtonSVG style={ButtonSVGType.Blue} />
    }

    case OperateButton.Long: {
      return <LongButtonSVG />
    }

    case OperateButton.BlueLong: {
      if (disabled) {
        return <LongButtonSVG />
      }

      return <LongButtonSVG style={ButtonSVGType.Blue} />
    }

    default: {
      return <LongButtonSVG />
    }
  }
}

const ApeironOperateButton: FC<Props> = props => {
  const { label, disabled, variant, className } = props

  if (!props.variant) {
    return <StyledBasicButton disableRipple {...props} />
  }

  return (
    <Container className={className}>
      <Button disableRipple {...props}>
        <SvgComponent disabled={disabled} variant={variant} />
        <Label>{label}</Label>
      </Button>
      <ApeironText>{label}</ApeironText>
    </Container>
  )
}

type SvgComponentProps = {
  variant?: OperateButton
  disabled?: boolean
}

type Props = ButtonBaseProps & {
  variant?: OperateButton
  label?: string
}

export type ApeironOperateButtonProps = ButtonBaseProps & {
  disableReason?: string
}

export default ApeironOperateButton
